package streetlight.model.data

import kampfire.api.Markdown
import kampfire.api.Slug
import kampfire.api.Username
import kampfire.api.toMarkdown
import kampfire.model.GeoPoint
import kampfire.model.Labeled
import kampfire.model.Url
import kampfire.model.toUrl
import koala.Image
import koala.model.RouteContent
import kotlin.time.Instant
import kotlinx.serialization.Serializable
import kotlin.jvm.JvmInline
import kotlin.uuid.Uuid

/** A place where events happen, such as a venue, park or corner. */
@Serializable
data class Location(
    override val locationId: LocationId,
    val cityId: CityId?,
    val mapId: MapId?,
    val timezoneId: String,
    val slug: Slug,
    val scout: Username?,
    val host: Username?,
    override val name: String?,
    val description: Markdown?,
    override val address: String?,
    override val city: String?,
    val state: String?,
    override val geoPoint: GeoPoint,
    val mapRank: Float?,
    val mapCategory: String?,
    val mapType: String?,
    val resources: Set<ResourceType>,
    val hours: HoursSchedule?,
    val website: Url?,
    val starCount: Int?,
    val eventCount: Int,
    val isLit: Boolean,
    val eventsUrl: Url?,
    override val image: Image?,
    val extraLinks: List<ExtraLink>?,
    val updatedAt: Instant,
    override val createdAt: Instant,
): Entity, LocationEntity, Labeled, RouteContent {

    val addressLine by lazy {
        addressLineOf(address, city)
    }

    override val label get() = name ?: address ?: "(geolocation)"
    override val sublabel get() = when (address) {
        null -> city
        else -> address
    }
    override val body get() = description

    override val links by lazy {
        buildList {
            website?.let {
                add(ExtraLink("website", it))
            }
            eventsUrl?.let {
                add(ExtraLink("calendar", it))
            }
            extraLinks?.let {
                addAll(it)
            }
        }.takeIf { it.isNotEmpty() }
    }
    override val markerId get() = locationId.toString()

    // td: implement contact info
    val phone: String? get() = null
    val email: String? get() = null
}

@JvmInline @Serializable
value class LocationId(override val value: Uuid): RecordId {
    override fun toString() = value.toString()

    companion object { fun random() = LocationId(Uuid.random())}
}

/** The parts of a location's address. */
@Serializable
data class LocationAddress(
    val streetAddress: String,
    val postCode: String? = null,
    val city: String? = null,
    val state: String? = null,
    val country: String? = null,
)

/** An edit of this location, starting from its current values. */
fun Location.toEdit() = LocationEdit(
    locationId = locationId,
    mapId = mapId,
    timezoneId = timezoneId,
    name = name,
    description = description,
    address = address,
    city = city,
    state = state,
    geoPoint = geoPoint,
    mapRank = mapRank, // td: track rank some other way
    mapCategory = mapCategory,
    mapType = mapType,
    resources = resources,
    hours = hours,
    website = website,
    eventsUrl = eventsUrl,
    extraLinks = extraLinks,
    image = image,
)

/** An edit of a new location from this place. */
fun PlaceProto.toEdit() = LocationEdit(
    name = name,
    address = address,
    city = city,
    geoPoint = geoPoint,
    website = website?.toUrl()
)

/** An edit of a new location from what was read of a page. */
fun LocationParse.toEdit(
    locationId: LocationId? = null
) = LocationEdit(
    locationId = locationId,
    name = name ?: "",
    description = description?.toMarkdown(),
    address = address,
//    val postalCode: String? = null,
    city = city,
//    val state: String? = null,
//    val country: String? = null,
    website = url?.toUrl(),
    eventsUrl = eventsUrl?.toUrl(),
    extraLinks = buildList {
        menuUrl?.let { add(ExtraLink("menu", it.toUrl())) }
        aboutUrl?.let { add(ExtraLink("about", it.toUrl()))}
    },
    image = imageUrl?.toUrl()?.let { Image(it) },
)

/** The address read of a page, or `null`. */
fun LocationParse.toAddress() = address?.let {
    LocationAddress(
        streetAddress = it,
        postCode = postalCode,
        city = city,
        state = state,
        country = country,
    )
}

